import { useEffect } from "react";
import { createPortal } from "react-dom";

// "Full view" modal shared by the Poll and Quiz cards.
//   meta     - small line above the title (type label, status chips)
//   title    - heading node
//   subtitle - optional line under the title (not part of the dialog's name)
//   trailing - optional node rendered at the top-right, before the close button
//   children - scrollable body
//   footer   - optional actions bar under the body, right-aligned (e.g. Cancel)
//   closeOnBackdrop - false keeps it open on a click outside (X and Esc still close)
// Closes via the X button, Esc, or a click on the backdrop.
export function FullViewModal({ labelId, meta, title, subtitle, trailing, footer, onClose, closeOnBackdrop = true, children }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeOnBackdrop ? onClose : undefined}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-100">
          <div className="min-w-0 flex-1">
            {meta && <div className="flex flex-wrap items-center gap-1.5 mb-1">{meta}</div>}
            <h3 id={labelId}>{title}</h3>
            {subtitle && <div className="mt-0.5">{subtitle}</div>}
          </div>
          {trailing}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 shrink-0"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5 overflow-y-auto space-y-5">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/60">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Status chip used on cards and in the modal header: emerald = active, gray = inactive.
export function StatusChip({ active }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-500 animate-pulse" : "bg-gray-400"}`} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

// "Full view" trigger button (neutral outline, expand icon). label: its text (the Poll card
// says "Poll details").
export function FullViewButton({ onClick, label = "Full view" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-200 text-[11px] font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900"
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
      </svg>
      {label}
    </button>
  );
}

export default FullViewModal;
